<script >
import ChartingLiteChart from '@/features/charting-next/components/ChartingLiteChart.vue';
import { formatNumberLabel } from '@/features/charting-next/adapters/chartingAdapterUtils';
import { computed, ref } from '@vue/composition-api';
const __sfc_main = {};
__sfc_main.props = {
  results: {
    type: Array,
    default: () => []
  },
  selectedTicker: {
    type: String,
    default: ''
  },
  selectedStock: {
    type: Object,
    default: null
  },
  chartSeries: {
    type: Object,
    default: () => ({
      candles: [],
      volumes: [],
      corporateActions: []
    })
  },
  isChartLoading: {
    type: Boolean,
    default: false
  },
  chartError: {
    type: [Object, String],
    default: ''
  },
  resolution: {
    type: String,
    default: 'M'
  },
  viewMode: {
    type: String,
    default: 'chart'
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  emptyMessage: {
    type: String,
    default: ''
  },
  sortOptions: {
    type: Array,
    default: () => []
  },
  sortValue: {
    type: String,
    default: 'original'
  },
  workspacesLabel: {
    type: String,
    default: 'Workspaces'
  },
  exploreLabel: {
    type: String,
    default: 'Explorar · Sin filtros'
  },
  tickerSearch: {
    type: String,
    default: ''
  },
  totalResults: {
    type: Number,
    default: 0
  },
  hasMoreResults: {
    type: Boolean,
    default: false
  },
  isLoadingNextPage: {
    type: Boolean,
    default: false
  },
  pageError: {
    type: [Object, String],
    default: null
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const formattedTotalResults = computed(() => formatNumberLabel(props.totalResults || props.results.length, {
    maximumFractionDigits: 0
  }));
  const emptyMiniChartItems = Object.freeze([]);
  const emptyMiniChartOverlays = Object.freeze({});
  const miniChartPriceSeriesType = ref('area');
  const miniChartPriceSeriesTypeOptions = Object.freeze([{
    value: 'area',
    label: 'Area',
    icon: 'pi pi-chart-line'
  }, {
    value: 'bars',
    label: 'Barras',
    icon: 'pi pi-chart-bar'
  }]);
  const miniChartOverlayVisibility = Object.freeze({
    support: false,
    stop: false,
    resistance: false,
    trendLine: false,
    trendPoint: false,
    riskRewardPoint: false
  });
  const submitTickerSearch = () => {
    if (String(props.tickerSearch || '').trim()) emit('submit-ticker-search');
  };
  const handleResultsScroll = event => {
    if (!props.hasMoreResults || props.isLoadingNextPage || !event?.target) return;
    const element = event.target;
    if (element.scrollHeight - element.scrollTop - element.clientHeight <= 180) emit('load-more');
  };
  return {
    emit,
    formattedTotalResults,
    emptyMiniChartItems,
    emptyMiniChartOverlays,
    miniChartPriceSeriesType,
    miniChartPriceSeriesTypeOptions,
    miniChartOverlayVisibility,
    submitTickerSearch,
    handleResultsScroll
  };
};
__sfc_main.components = Object.assign({
  ChartingLiteChart
}, __sfc_main.components);
export default __sfc_main;
</script>

<template>
    <aside class="charting-workspace-results-column">
        <div class="charting-workspace-results-toolbar">
            <div class="charting-workspace-view-switch" aria-label="Vista del graficador">
                <button type="button" :class="{ 'is-active': viewMode === 'chart' }" @click="emit('change-view-mode', 'chart')">
                    <i class="pi pi-chart-line" aria-hidden="true"></i>
                    <span>Gráfico</span>
                </button>
                <button type="button" :class="{ 'is-active': viewMode === 'table' }" @click="emit('change-view-mode', 'table')">
                    <i class="pi pi-table" aria-hidden="true"></i>
                    <span>Tabla</span>
                </button>
            </div>

            <form class="charting-workspace-ticker-search" @submit.prevent="submitTickerSearch">
                <InputText :model-value="tickerSearch" aria-label="Abrir ticker" placeholder="Ticker…" @update:model-value="emit('update:ticker-search', $event)" />
                <Button type="submit" icon="pi pi-arrow-right" aria-label="Abrir ticker" :disabled="!tickerSearch.trim()" />
            </form>
            <Button :label="exploreLabel" icon="pi pi-filter" severity="secondary" outlined class="charting-workspace-results-action" @click="emit('open-search')" />
            <Button :label="workspacesLabel" icon="pi pi-folder-open" severity="secondary" outlined class="charting-workspace-results-action" @click="emit('open-workspaces')" />
        </div>

        <section class="card !mb-0 charting-workspace-results-card charting-workspace-results-card-list">
            <div class="charting-workspace-results-body">
                <div v-if="viewMode !== 'table' && sortOptions.length > 1" class="charting-workspace-results-sort">
                    <label class="charting-workspace-results-counter-label" for="charting-workspace-results-sort">Orden</label>
                    <select id="charting-workspace-results-sort" class="charting-workspace-native-select" :value="sortValue" :disabled="isLoading || !results.length" @change="emit('change-sort', $event.target.value)">
                        <option v-for="option in sortOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>
                </div>

                <div v-if="viewMode !== 'table'" class="charting-workspace-results-counter">
                    <div class="charting-workspace-results-counter-label">Resultados</div>
                    <div class="charting-workspace-results-counter-value">{{ isLoading ? 'Actualizando...' : `${formattedTotalResults} valores` }}</div>
                </div>

                <div v-if="isLoading" class="charting-workspace-results-status">
                    <ProgressSpinner style="width: 32px; height: 32px" stroke-width="6" />
                </div>
                <div v-else-if="!results.length" class="charting-workspace-results-empty">
                    <div class="brand-empty-state rounded-xl border border-dashed p-4 text-sm">
                        {{ emptyMessage || 'No hay valores visibles para la fuente actual.' }}
                    </div>
                </div>
                <div v-else-if="viewMode === 'table'" class="charting-workspace-results-table-aside">
                    <div class="charting-workspace-results-selected">
                        <div class="charting-workspace-results-selected__eyebrow">Valor seleccionado</div>
                        <div class="charting-workspace-results-selected__identity">
                            <div class="charting-workspace-results-selected__ticker">{{ (selectedStock && selectedStock.ticker) || selectedTicker || 'N/D' }}</div>
                            <slot name="stock-actions" />
                        </div>
                        <div class="charting-workspace-results-selected__name">{{ (selectedStock && (selectedStock.companyName || selectedStock.description)) || 'Sin nombre disponible' }}</div>
                        <div class="charting-workspace-results-selected__meta">
                            <span v-if="selectedStock && selectedStock.entryTypeLabel">{{ selectedStock.entryTypeLabel }}</span>
                            <span v-if="selectedStock && selectedStock.country">{{ selectedStock.country }}</span>
                            <span v-if="selectedStock && selectedStock.indexName">{{ selectedStock.indexName }}</span>
                        </div>
                    </div>

                    <div class="charting-workspace-results-mini-mode" aria-label="Vista del minigráfico">
                        <button v-for="option in miniChartPriceSeriesTypeOptions" :key="option.value" type="button" :class="{ 'is-active': miniChartPriceSeriesType === option.value }" @click="miniChartPriceSeriesType = option.value">
                            <i :class="option.icon" aria-hidden="true"></i>
                            <span>{{ option.label }}</span>
                        </button>
                    </div>

                    <div class="charting-workspace-results-mini-chart">
                        <div v-if="isChartLoading" class="charting-workspace-results-mini-status">
                            <ProgressSpinner style="width: 24px; height: 24px" stroke-width="6" />
                        </div>
                        <div v-else-if="chartError" class="charting-workspace-results-mini-status text-xs text-muted-color">No se pudo cargar el gráfico.</div>
                        <div v-else-if="!chartSeries || !chartSeries.candles || !chartSeries.candles.length" class="charting-workspace-results-mini-status text-xs text-muted-color">Sin velas disponibles.</div>
                        <ChartingLiteChart
                            v-else
                            :candles="chartSeries.candles"
                            :volumes="emptyMiniChartItems"
                            :corporate-actions="emptyMiniChartItems"
                            :analysis-overlays="emptyMiniChartOverlays"
                            :overlay-visibility="miniChartOverlayVisibility"
                            :resolution="resolution"
                            :price-series-type="miniChartPriceSeriesType"
                            :show-volume="false"
                            :show-legend="false"
                            compact
                            :ticker="(selectedStock && selectedStock.ticker) || selectedTicker || ''"
                            :company-name="(selectedStock && (selectedStock.companyName || selectedStock.description)) || ''"
                            :entry-type-label="(selectedStock && selectedStock.entryTypeLabel) || ''"
                            :series-key="`mini:${(selectedStock && selectedStock.ticker) || selectedTicker || 'none'}:${resolution}:${miniChartPriceSeriesType}`"
                        />
                    </div>
                </div>
                <div v-else class="charting-workspace-results-list" @scroll.passive="handleResultsScroll">
                    <button v-for="item in results" :key="item.id" type="button" class="charting-workspace-result-row" :class="{ 'is-selected': item.ticker === selectedTicker }" @click="emit('select-ticker', item.ticker)">
                        <div class="charting-workspace-result-row__content">
                            <div class="charting-workspace-result-row__main">
                                <div class="charting-workspace-result-row__ticker">{{ item.ticker }}</div>
                                <span class="charting-workspace-result-row__separator">·</span>
                                <div class="charting-workspace-result-row__company">{{ item.companyName || item.description || 'N/D' }}</div>
                            </div>
                            <div v-if="item.description && item.description !== item.companyName" class="charting-workspace-result-row__description">{{ item.description }}</div>
                        </div>
                    </button>
                    <div v-if="isLoadingNextPage" class="charting-workspace-results-page-state">Cargando más valores…</div>
                    <button v-else-if="pageError" type="button" class="charting-workspace-results-page-retry" @click="emit('retry')">Reintentar carga</button>
                </div>
            </div>
        </section>
    </aside>
</template>

<style scoped>
.charting-workspace-results-selected__identity { display: flex; align-items: center; gap: 0.5rem; }
.charting-workspace-results-toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
}

.charting-workspace-ticker-search {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.35rem;
}

::v-deep .charting-workspace-ticker-search .p-inputtext {
    min-width: 0;
    width: 100%;
}

.charting-workspace-results-page-state,
.charting-workspace-results-page-retry {
    flex: 0 0 auto;
    padding: 0.7rem;
    text-align: center;
    color: var(--p-text-muted-color);
    font-size: 0.76rem;
}

.charting-workspace-results-page-retry {
    border: 0;
    background: transparent;
    color: var(--brand-primary);
    cursor: pointer;
    text-decoration: underline;
}

.charting-workspace-view-switch {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.2rem;
    border: 1px solid var(--brand-panel-border);
    border-radius: 0.95rem;
    background: var(--charting-b-panel-bg, rgba(248, 250, 252, 0.86));
    padding: 0.2rem;
}

.charting-workspace-view-switch button {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    border: 0;
    border-radius: 0.72rem;
    background: transparent;
    padding: 0.48rem 0.45rem;
    color: var(--p-text-muted-color);
    font-size: 0.78rem;
    font-weight: 800;
    line-height: 1;
    cursor: pointer;
    transition:
        background-color 0.18s ease,
        color 0.18s ease,
        box-shadow 0.18s ease;
}

.charting-workspace-view-switch button.is-active {
    background: var(--charting-b-panel-bg-strong, rgba(255, 255, 255, 0.92));
    color: var(--p-text-color);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

::v-deep .charting-workspace-results-action.p-button {
    width: 100%;
    justify-content: flex-start;
    padding: 0.62rem 0.75rem;
    border: 1px solid var(--brand-panel-border);
    border-radius: 0.95rem;
    background: var(--charting-b-panel-bg-strong, rgba(255, 255, 255, 0.92));
    background-clip: padding-box;
    color: var(--p-text-color);
    cursor: pointer;
    transition:
        border-color 0.18s ease,
        background-color 0.18s ease,
        box-shadow 0.18s ease;
}

::v-deep .charting-workspace-results-action.p-button:not(:disabled):hover {
    border-color: color-mix(in srgb, var(--brand-primary), transparent 38%);
    background: var(--charting-b-hover-bg, rgba(224, 242, 254, 0.68));
    color: var(--p-text-color);
    box-shadow: 0 14px 30px rgba(14, 165, 233, 0.12);
}

::v-deep .charting-workspace-results-action.p-button:focus-visible {
    box-shadow:
        0 14px 30px rgba(14, 165, 233, 0.12),
        0 0 0 0.12rem color-mix(in srgb, var(--brand-primary), transparent 72%);
}

.charting-workspace-results-counter {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.65rem;
    padding: 0 0.05rem;
}

.charting-workspace-results-counter-label {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--p-text-muted-color);
}

.charting-workspace-results-counter-value {
    font-size: 0.82rem;
    font-weight: 700;
    color: var(--p-text-color);
}

.charting-workspace-results-sort {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    border: 1px solid var(--charting-b-border-color);
    border-radius: 0.85rem;
    background: var(--charting-b-panel-bg);
    padding: 0.42rem 0.5rem 0.42rem 0.62rem;
}

.charting-workspace-results-sort .charting-workspace-native-select {
    min-width: 0;
    flex: 1 1 auto;
    border-radius: 0.78rem;
    padding: 0.42rem 0.58rem;
    font-size: 0.78rem;
}

.charting-workspace-results-table-aside {
    display: flex;
    min-height: 0;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 0.65rem;
}

.charting-workspace-results-selected {
    border: 1px solid var(--charting-b-border-color);
    border-radius: 0.92rem;
    background: var(--charting-b-panel-bg-strong);
    padding: 0.75rem;
}

.charting-workspace-results-selected__eyebrow {
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--p-text-muted-color);
}

.charting-workspace-results-selected__ticker {
    margin-top: 0.35rem;
    font-size: 1.05rem;
    font-weight: 850;
    line-height: 1.1;
    color: var(--p-text-color);
}

.charting-workspace-results-selected__name {
    margin-top: 0.22rem;
    overflow: hidden;
    color: var(--p-text-color);
    font-size: 0.78rem;
    font-weight: 650;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.charting-workspace-results-selected__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.28rem;
    margin-top: 0.55rem;
}

.charting-workspace-results-selected__meta span {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    border: 1px solid var(--charting-b-border-color);
    border-radius: 999px;
    padding: 0.18rem 0.42rem;
    color: var(--p-text-muted-color);
    font-size: 0.68rem;
    font-weight: 750;
    line-height: 1.1;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.charting-workspace-results-mini-mode {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.18rem;
    border: 1px solid var(--charting-b-border-color);
    border-radius: 0.82rem;
    background: var(--charting-b-panel-bg);
    padding: 0.18rem;
}

.charting-workspace-results-mini-mode button {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    border: 0;
    border-radius: 0.64rem;
    background: transparent;
    padding: 0.38rem 0.4rem;
    color: var(--p-text-muted-color);
    font-size: 0.72rem;
    font-weight: 800;
    line-height: 1;
    cursor: pointer;
    transition:
        background-color 0.18s ease,
        color 0.18s ease;
}

.charting-workspace-results-mini-mode button.is-active {
    background: var(--charting-b-panel-bg-strong);
    color: var(--p-text-color);
}

.charting-workspace-results-mini-chart {
    min-height: 12rem;
    flex: 1 1 auto;
    overflow: hidden;
    border: 1px solid var(--charting-b-border-color);
    border-radius: 0.92rem;
    background: var(--charting-b-panel-bg-strong);
}

.charting-workspace-results-mini-status {
    display: flex;
    height: 100%;
    min-height: 12rem;
    align-items: center;
    justify-content: center;
    padding: 0.75rem;
    text-align: center;
}

.charting-workspace-result-row__content {
    min-width: 0;
    width: 100%;
}

.charting-workspace-result-row__main {
    display: flex;
    min-width: 0;
    align-items: baseline;
    gap: 0.35rem;
}

.charting-workspace-result-row__ticker {
    flex: 0 0 auto;
    font-size: 0.86rem;
    font-weight: 700;
    line-height: 1.15;
}

.charting-workspace-result-row__separator {
    flex: 0 0 auto;
    color: var(--p-text-muted-color);
    font-size: 0.76rem;
    font-weight: 700;
    line-height: 1.15;
}

.charting-workspace-result-row__company {
    min-width: 0;
    overflow: hidden;
    color: var(--p-text-color);
    font-size: 0.76rem;
    font-weight: 600;
    line-height: 1.15;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.charting-workspace-result-row__description {
    overflow: hidden;
    margin-top: 0.16rem;
    color: var(--p-text-muted-color);
    font-size: 0.72rem;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
